import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';

const API_BASE_URL = '/api';

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notification = useNotification();

  const [token] = useState(searchParams.get('token') || '');
  const [type] = useState(searchParams.get('type') || 'invitation');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  // Typed reason from backend: 'USED' | 'EXPIRED' | 'INVALID'. Drives the
  // heading + CTA so a re-used invite says "already set up — sign in"
  // instead of the misleading "expired link" message.
  const [tokenReason, setTokenReason] = useState('INVALID');
  const [tokenInfo, setTokenInfo] = useState(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({ password: '', confirmPassword: '' });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenError('No token provided');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/validate-token?token=${encodeURIComponent(token)}`);
        const data = await response.json();

        if (data.valid) {
          setTokenValid(true);
          setTokenInfo(data);
        } else {
          setTokenError(data.error || 'Invalid token');
          setTokenReason(data.reason || 'INVALID');
        }
      } catch (error) {
        console.error('Failed to validate token:', error);
        setTokenError('Failed to validate token. Please try again.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const validate = () => {
    const newErrors = { password: '', confirmPassword: '' };
    let isValid = true;

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
      isValid = false;
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        notification.success('Password set successfully!');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        notification.error(data.error || 'Failed to set password');
      }
    } catch (error) {
      console.error('Failed to set password:', error);
      notification.error('Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (type === 'reset') {
      return 'Reset Your Password';
    }
    return 'Set Your Password';
  };

  const getSubtitle = () => {
    if (type === 'reset') {
      return 'Enter a new password for your account';
    }
    return 'Create a password to complete your account setup';
  };

  // Loading state
  if (validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/login_verops.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">Validating your link...</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Invalid token state — branch on the typed reason so the message
  // matches reality. USED is the most common false-alarm: the operator
  // already clicked the invite and set their password, but the link
  // sat in their inbox and they revisited it. Showing "expired" there
  // is confusing; the right answer is "you're done, go sign in".
  if (!tokenValid) {
    const reasonView = (() => {
      if (tokenReason === 'USED') {
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />,
          title: "You're all set",
          body: tokenError || "This link has already been used. Please sign in with your password.",
          cta: 'Go to sign in',
        };
      }
      if (tokenReason === 'EXPIRED') {
        return {
          icon: <ErrorIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />,
          title: 'This link has expired',
          body: tokenError || 'Invitation and reset links are valid for a limited time. Please request a new one.',
          cta: 'Go to sign in',
        };
      }
      return {
        icon: <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />,
        title: 'Invalid link',
        body: tokenError || 'This link is not valid. Double-check the URL or request a new one.',
        cta: 'Go to sign in',
      };
    })();

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/login_verops.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            {reasonView.icon}
            <Typography variant="h5" gutterBottom>
              {reasonView.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {reasonView.body}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
            >
              {reasonView.cta}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/login_verops.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Password Set Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can now sign in with your new password.
              Redirecting to login...
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
            >
              Go to Login Now
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Main form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/login_verops.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: 3,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
      >
        <CardContent sx={{ p: 5 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/realops.png`}
              alt="Logo"
              style={{ width: "170px", height: "auto", marginBottom: '16px' }}
            />
            <Typography variant="h5" gutterBottom fontWeight="600">
              {getTitle()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getSubtitle()}
            </Typography>
          </Box>

          {/* User Info */}
          {tokenInfo?.username && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Setting password for: <strong>{tokenInfo.username}</strong>
            </Alert>
          )}

          {/* Password Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(errors.password)}
              helperText={errors.password || 'At least 4 characters'}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="new-password"
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoComplete="new-password"
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                height: 48,
                fontSize: '1rem',
                fontWeight: 500,
                position: 'relative',
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Set Password'
              )}
            </Button>
          </form>

          {/* Footer */}
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 3 }}
          >
            <Button
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
            >
              Back to Login
            </Button>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SetPasswordPage;
